// utils/cdrTimezone.js
//
// Shared helpers for the CDR Report timezone selector (admin + redirect portals).
//
// The backend (`webapi/libs/app_helper.to_ist_iso`) tags every CDR datetime
// in the API response with an explicit `+05:30` IST offset. That makes the
// value unambiguous — `dayjs(value)` parses it to the correct UTC instant
// regardless of the browser's local TZ, and `.tz(selectedTz)` then renders
// it in whichever zone the user picked.
//
// Three output flavors:
//   formatDateTimeInTz()      — plain string for display + MUI valueFormatter
//   formatDateTimePartsInTz() — { datePart, timePart, abbr } for the two-span
//                                JSX renderers that color date vs time
//   formatDateForCSVInTz()    — wrapped in `="\t..."` so Excel doesn't auto-
//                                format the cell into a real date.
//
// Default UI timezone resolution prefers, in order:
//   1. user's saved choice in localStorage (if still in the curated list)
//   2. browser-detected timezone (if in the curated list)
//   3. Asia/Kolkata (project default)

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const TIMEZONE_OPTIONS = [
  { value: "UTC",                  label: "UTC — Coordinated Universal Time" },
  { value: "Asia/Kolkata",         label: "IST — India (Asia/Kolkata)" },
  { value: "America/New_York",     label: "EST/EDT — US Eastern (New York)" },
  { value: "America/Chicago",      label: "CST/CDT — US Central (Chicago)" },
  { value: "America/Denver",       label: "MST/MDT — US Mountain (Denver)" },
  { value: "America/Los_Angeles",  label: "PST/PDT — US Pacific (Los Angeles)" },
  { value: "Europe/London",        label: "GMT/BST — UK (London)" },
  { value: "Europe/Paris",         label: "CET/CEST — Central Europe (Paris)" },
  { value: "Australia/Sydney",     label: "AEST/AEDT — Australia (Sydney)" },
  { value: "Asia/Tokyo",           label: "JST — Japan (Tokyo)" },
  { value: "Asia/Dubai",           label: "GST — Gulf (Dubai)" },
  { value: "Asia/Singapore",       label: "SGT — Singapore" },
];

const TIMEZONE_VALUES = new Set(TIMEZONE_OPTIONS.map((o) => o.value));

export const LOCAL_STORAGE_KEY = "cdr_report_tz";
const FALLBACK_TZ = "Asia/Kolkata";

// Intl short-name output is mostly reliable on modern browsers but sometimes
// returns "GMT+5:30" / "GMT+9" instead of the canonical abbreviation. Override
// the well-known zones so the report stays self-describing on any device.
const MANUAL_ABBR_MAP = {
  "UTC": "UTC",
  "Asia/Kolkata": "IST",
  "Asia/Tokyo": "JST",
  "Asia/Dubai": "GST",
  "Asia/Singapore": "SGT",
};

const isValidTz = (tz) => typeof tz === "string" && TIMEZONE_VALUES.has(tz);

export const getDefaultTimezone = () => {
  try {
    const saved = typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(LOCAL_STORAGE_KEY)
      : null;
    if (isValidTz(saved)) return saved;
  } catch (_) { /* localStorage unavailable */ }

  try {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (isValidTz(browserTz)) return browserTz;
  } catch (_) { /* Intl unavailable */ }

  return FALLBACK_TZ;
};

export const saveTimezone = (tz) => {
  if (!isValidTz(tz)) return;
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, tz);
    }
  } catch (_) { /* swallow */ }
};

// Parses an API datetime value into a dayjs at the right UTC instant.
// The backend always sends an offset-aware ISO string (see app_helper.to_ist_iso),
// so dayjs's default parser picks up the offset and anchors the instant.
// Falls back gracefully for legacy / null values.
const parseDbDatetime = (value) => {
  if (value == null || value === "") return null;
  const m = dayjs(value);
  return m.isValid() ? m : null;
};

export const getTzAbbreviation = (valueOrDayjs, tz) => {
  if (MANUAL_ABBR_MAP[tz]) return MANUAL_ABBR_MAP[tz];
  try {
    let date;
    if (valueOrDayjs && typeof valueOrDayjs.toDate === "function") {
      // dayjs object — already anchored at the right instant.
      date = valueOrDayjs.toDate();
    } else if (valueOrDayjs instanceof Date) {
      date = valueOrDayjs;
    } else if (valueOrDayjs) {
      const m = parseDbDatetime(valueOrDayjs);
      date = m ? m.toDate() : null;
    } else {
      date = new Date();
    }
    if (!date || isNaN(date.getTime())) return "";
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart ? tzPart.value : "";
  } catch (_) {
    return "";
  }
};

export const formatDateTimePartsInTz = (value, tz) => {
  const m = parseDbDatetime(value);
  if (!m) return null;
  const local = m.tz(tz);
  return {
    datePart: local.format("DD/MM/YYYY"),
    timePart: local.format("HH:mm:ss"),
    abbr: getTzAbbreviation(m, tz),
  };
};

export const formatDateTimeInTz = (value, tz, { emptyText = "-" } = {}) => {
  const parts = formatDateTimePartsInTz(value, tz);
  if (!parts) return emptyText;
  const suffix = parts.abbr ? ` (${parts.abbr})` : "";
  return `${parts.datePart} ${parts.timePart}${suffix}`;
};

export const formatDateForCSVInTz = (value, tz) => {
  const parts = formatDateTimePartsInTz(value, tz);
  if (!parts) return "";
  const suffix = parts.abbr ? ` (${parts.abbr})` : "";
  // Match existing formatDateForCSV: leading ="\t prevents Excel from auto-
  // converting the cell into a real date. Trailing " closes the formula.
  return `="\t${parts.datePart} ${parts.timePart}${suffix}"`;
};

// Helper for filenames: short abbreviation suitable for filesystem use.
export const getCurrentTzAbbreviation = (tz) =>
  getTzAbbreviation(new Date(), tz) || "TZ";
